import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { playStoreUrl, playBadgeSrc } from '@/utils/playStore';
import {
  Shield,
  Users,
  CheckCircle,
  Scale,
  ChevronLeft,
  Lock,
  MessageCircle,
  UserCheck,
  Quote,
  FileText,
  Heart,
  User,
  UserPlus,
  Eye,
  ShieldCheck,
  Clock,
  Award,
  Bell,
  FileCheck,
  UserX,
  AlertCircle,
  CheckCircle2,
  PhoneCall,
  Key,
  LogIn,
  Facebook,
  Instagram,
  Send,
  Sparkles,
  Mic,
  Calendar
} from 'lucide-react';
import SEO from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import SuccessStoriesStrip from '@/components/stories/SuccessStoriesStrip';
import { articlesBySlug } from '@/data/articles';
import { brandName } from '@/utils/scriptConventions';

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Lock,
      title: t('landing.feat1Title'),
      description: t('landing.feat1Desc'),
      color: 'bg-wefaqna-green/10',
    },
    {
      icon: Users,
      title: t('landing.feat2Title'),
      description: t('landing.feat2Desc'),
      color: 'bg-wefaqna-navy/10',
    },
    {
      icon: CheckCircle,
      title: t('landing.feat3Title'),
      description: t('landing.feat3Desc'),
      color: 'bg-wefaqna-gold/10',
    },
    {
      icon: Scale,
      title: t('landing.feat4Title'),
      description: t('landing.feat4Desc'),
      color: 'bg-wefaqna-green/10',
    },
  ];

  const safetyFeatures = [
    {
      icon: ShieldCheck,
      title: t('landing.safetyF1Title'),
      description: t('landing.safetyF1Desc'),
      color: 'from-wefaqna-green/10 to-wefaqna-green/5',
    },
    {
      icon: Lock,
      title: t('landing.safetyF2Title'),
      description: t('landing.safetyF2Desc'),
      color: 'from-wefaqna-navy/10 to-wefaqna-navy/5',
    },
    {
      icon: Eye,
      title: t('landing.safetyF3Title'),
      description: t('landing.safetyF3Desc'),
      color: 'from-wefaqna-gold/10 to-wefaqna-gold/5',
    },
    {
      icon: UserX,
      title: t('landing.safetyF4Title'),
      description: t('landing.safetyF4Desc'),
      color: 'from-red-50 to-red-25',
    },
    {
      icon: Bell,
      title: t('landing.safetyF5Title'),
      description: t('landing.safetyF5Desc'),
      color: 'from-wefaqna-green/10 to-wefaqna-green/5',
    },
    {
      icon: FileCheck,
      title: t('landing.safetyF6Title'),
      description: t('landing.safetyF6Desc'),
      color: 'from-wefaqna-navy/10 to-wefaqna-navy/5',
    },
  ];

  const parentTrustPoints = [
    {
      icon: Shield,
      title: t('landing.trustP1Title'),
      description: t('landing.trustP1Desc'),
    },
    {
      icon: Key,
      title: t('landing.trustP2Title'),
      description: t('landing.trustP2Desc'),
    },
    {
      icon: PhoneCall,
      title: t('landing.trustP3Title'),
      description: t('landing.trustP3Desc'),
    },
    {
      icon: FileCheck,
      title: t('landing.trustP4Title'),
      description: t('landing.trustP4Desc'),
    },
  ];

  const testimonials = [
    {
      quote: t('landing.testimonial0Quote'),
      author: t('landing.testimonial0Author'),
      location: t('landing.testimonial0Location'),
    },
    {
      quote: t('landing.testimonial1Quote'),
      author: t('landing.testimonial1Author'),
      location: t('landing.testimonial1Location'),
    },
    {
      quote: t('landing.testimonial2Quote'),
      author: t('landing.testimonial2Author'),
      location: t('landing.testimonial2Location'),
    },
    {
      quote: t('landing.testimonial3Quote'),
      author: t('landing.testimonial3Author'),
      location: t('landing.testimonial3Location'),
    },
    {
      quote: t('landing.testimonial4Quote'),
      author: t('landing.testimonial4Author'),
      location: t('landing.testimonial4Location'),
    },
    {
      quote: t('landing.testimonial5Quote'),
      author: t('landing.testimonial5Author'),
      location: t('landing.testimonial5Location'),
    },
    {
      quote: t('landing.testimonial6Quote'),
      author: t('landing.testimonial6Author'),
      location: t('landing.testimonial6Location'),
    },
    {
      quote: t('landing.testimonial7Quote'),
      author: t('landing.testimonial7Author'),
      location: t('landing.testimonial7Location'),
    },
    {
      quote: t('landing.testimonial8Quote'),
      author: t('landing.testimonial8Author'),
      location: t('landing.testimonial8Location'),
    },
  ];

  const trustBadges = [
    { icon: Eye, text: t('landing.badge1Text') },
    { icon: ShieldCheck, text: t('landing.badge2Text') },
    { icon: MessageCircle, text: t('landing.badge3Text') },
    { icon: UserCheck, text: t('landing.badge4Text') },
  ];

  const statistics = [
    { number: '+5000', label: t('landing.statFamiliesLabel'), icon: Users },
    { number: '+2800', label: t('landing.statActiveProfilesLabel'), icon: FileText },
    { number: '+950', label: t('landing.statContactsLabel'), icon: MessageCircle },
    { number: '127', label: t('landing.statMarriagesLabel'), icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="موقع زواج حلال مجاني للمسلمين - بموافقة الأهل بدون صور | Free Halal Muslim Marriage App" description="موقع زواج حلال مجاني للأسر المسلمة في مصر والعالم العربي والمهجر. بموافقة الأهل وبدون صور منشورة. Free halal Muslim marriage app — family-supervised matchmaking for muslim marriage worldwide, islamic dating alternative, ID-verified profiles. Egypt, Saudi, UAE, Syria, Morocco, UK, Germany, USA diaspora. التسجيل مجاني ١٠٠٪." path="/" />
      {/* The site-wide Organization and WebSite nodes live in
          `public/index.html`, which every page already serves. This page used
          to emit a SECOND pair of both: a weaker Organization (a different
          logo, a different description, and `sameAs: ['https://wefaqna.com']`
          pointing at itself) alongside the richer one. Two Organization nodes
          claiming the same url are competing entity claims, not a stronger
          one, so the duplicate is gone rather than merged. */}
      {/* Hero Section - Mobile-First, Full Width Impact */}
      <section className="relative bg-white overflow-hidden border-b-4 border-wefaqna-green/20">
        {/* Decorative Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(51, 62, 72, 0.1) 35px, rgba(51, 62, 72, 0.1) 70px)`,
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          {/* Trust Badge Bar - Mobile Optimized */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8 sm:mb-10 flex-wrap">
            <div className="flex items-center gap-1.5 bg-wefaqna-green/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 border-2 border-wefaqna-green/30">
              <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-wefaqna-forest" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-bold">{t('landing.trustChipTrusted')}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-wefaqna-gold/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 border-2 border-wefaqna-gold/30">
              <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-wefaqna-gold" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-bold">{t('landing.trustChipFamily')}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-wefaqna-navy/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-2 border-2 border-wefaqna-navy/30">
              <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-wefaqna-navy" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-bold">{t('landing.trustChipPrivacy')}</span>
            </div>
          </div>

          {/* Logo & Platform Name */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center justify-center mb-4 sm:mb-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-wefaqna-green to-wefaqna-green/80 rounded-full flex items-center justify-center shadow-2xl shadow-wefaqna-green/40 animate-fade-in-up ring-4 ring-wefaqna-green/20">
                <Heart className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white fill-white" />
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-wefaqna-navy leading-tight mb-3 sm:mb-4 animate-fade-in-up">
              {/* ONE sentence with the name as a slot: Urdu and Turkish put the
                  name first ("وفّقنا میں خوش آمدید", "Wefaqna'ya hoş geldiniz"),
                  which a prefix + name in English order cannot say. */}
              <Trans
                i18nKey="landing.heroTitle"
                values={{ brand: brandName(i18n.language) }}
                components={{ brand: <span className="text-wefaqna-forest" /> }}
              />
            </h1>

            <p className="text-xl sm:text-2xl lg:text-3xl text-wefaqna-forest font-bold mb-4 sm:mb-6 animate-fade-in-up tracking-wide">
              {t('landing.heroTagline')}
            </p>

            <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed animate-fade-in-up px-4 font-medium">
              {t('landing.heroSubtagline')}
            </p>

            {/* Official Google Play badge + QR code (desktop only). */}
            <div className="w-full flex flex-col items-center mb-3 sm:mb-4">
              <a
                href={playStoreUrl({ medium: 'site_landing_hero' })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                data-testid="download-app-btn"
                aria-label={t('footer.downloadAppAria')}
              >
                <img
                  src={playBadgeSrc(i18n.language)}
                  alt={t('footer.downloadAppAlt')}
                  width={200}
                  height={60}
                  className="h-14 sm:h-16 w-auto"
                />
              </a>
              <div className="hidden sm:flex flex-col items-center gap-2 mt-4">
                <p className="text-xs text-gray-600 font-medium">{t('landing.qrScanFromMobile')}</p>
                <img
                  src="/play-store-qr.png"
                  alt={t('faqExtra.qrAltText')}
                  width={140}
                  height={140}
                  className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm"
                  loading="lazy"
                />
                <p className="text-[10px] text-gray-500">{t('landing.iosSoonShort')}</p>
              </div>
            </div>

            {/* Privacy trust signal - secondary inline hint */}
            <div className="flex items-center justify-center gap-1.5 mb-8 sm:mb-10 text-gray-700">
              <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-wefaqna-forest flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium">{t('landing.noPhotosTagline')}</span>
            </div>
          </div>

          {/* Primary CTA Buttons - Mobile Optimized, Full Thumb Coverage */}
          <div className="max-w-3xl mx-auto mb-6 sm:mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-5 animate-fade-in-up">
              <Button
                onClick={() => navigate('/register?type=male')}
                className="w-full bg-wefaqna-navy text-white hover:bg-wefaqna-navy/90 font-bold text-base sm:text-lg rounded-2xl px-6 py-6 sm:py-7 shadow-2xl hover:shadow-wefaqna-navy/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 group"
                data-testid="register-male-btn"
              >
                <User className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform" />
                <span>{t('landing.registerGroomCta')}</span>
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 rtl-flip group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                onClick={() => navigate('/register?type=female')}
                className="w-full bg-wefaqna-forest text-white hover:bg-wefaqna-forest-hover font-bold text-base sm:text-lg rounded-2xl px-6 py-6 sm:py-7 shadow-2xl shadow-wefaqna-green/20 hover:shadow-wefaqna-green/40 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 group"
                data-testid="register-female-btn"
              >
                <Users className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform" />
                <span>{t('landing.registerBrideCta')}</span>
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 rtl-flip group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Bride-path clarifier. guardian_info became OPTIONAL in v1.2.42+125:
                she registers herself and invites her wali if she wants. The old
                "(عن طريق الأسرة)" CTA framed family as the entry gate, which the
                bride-churn analysis tied to the 14.3% bail at the guardian step.
                Family reassurance stays — involving the wali is welcome, not required. */}
            <div className="flex items-center justify-center gap-2 bg-wefaqna-forest/5 rounded-xl px-4 py-3 border border-wefaqna-forest/20 mb-4 animate-fade-in-up">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-forest flex-shrink-0" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-semibold text-center">
                {t('landing.brideSelfRegisterNote')}
              </span>
            </div>

            {/* Management Review Notice for Guys */}
            <div className="flex items-center justify-center gap-2 bg-wefaqna-navy/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-wefaqna-navy/20 mb-4 animate-fade-in-up">
              <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-navy flex-shrink-0" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-semibold text-center">
                {t('landing.adminReviewNotice')}
              </span>
            </div>

            {/* Secondary CTA - Browse */}
            <Button
              onClick={() => navigate('/browse')}
              variant="outline"
              className="w-full border-2 border-wefaqna-navy/40 text-wefaqna-navy hover:bg-wefaqna-navy hover:text-white font-semibold text-base sm:text-lg rounded-2xl px-6 py-5 sm:py-6 transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] backdrop-blur-sm animate-fade-in-up shadow-md"
              data-testid="browse-forms-btn"
            >
              <FileText className="h-5 w-5 ms-2" />
              {t('landing.browseProfilesBtn')}
            </Button>

            {/* Sign-in hint for returning users */}
            {!isAuthenticated && (
              <div className="text-center mt-4 animate-fade-in-up">
                <span className="text-sm text-gray-600">{t('landing.alreadyHaveAccount')} </span>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm font-bold text-wefaqna-navy underline underline-offset-4 decoration-2 decoration-wefaqna-green hover:text-wefaqna-forest transition-colors inline-flex items-center gap-1"
                  data-testid="hero-signin-link"
                >
                  <LogIn className="h-4 w-4" />
                  {t('auth.loginTitle')}
                </button>
              </div>
            )}
          </div>

          {/* Trust Badges Grid - Mobile Optimized */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto animate-fade-in-up">
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-2 text-center bg-gradient-to-br from-gray-50 to-white backdrop-blur-sm rounded-xl p-4 sm:p-5 border-2 border-gray-200 hover:border-wefaqna-green/40 hover:shadow-lg transition-all duration-300 hover:scale-105"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-wefaqna-green/20 rounded-full flex items-center justify-center">
                  <badge.icon className="h-5 w-5 sm:h-6 sm:w-6 text-wefaqna-forest" />
                </div>
                <span className="text-wefaqna-navy text-xs sm:text-sm font-bold leading-tight">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section - High Impact */}
      <section className="py-10 sm:py-12 lg:py-16 bg-gradient-to-b from-wefaqna-navy/95 to-wefaqna-navy border-y-4 border-wefaqna-green/30" data-testid="statistics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-wefaqna-green/20 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 mb-4">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-green" />
              <span className="text-wefaqna-green text-xs sm:text-sm font-bold">{t('landing.trustedAndMonitored')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
              {t('landing.statsHeading')}
            </h2>
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg">{t('landing.thousandsTrust')}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
            {statistics.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-fade-in-up bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 lg:p-8 border-2 border-wefaqna-green/20 hover:border-wefaqna-green/50 hover:bg-white/10 transition-all duration-300 hover:scale-105 shadow-xl"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`stat-${index}`}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-wefaqna-green/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ring-4 ring-wefaqna-green/10">
                  <stat.icon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-wefaqna-green" />
                </div>
                <p className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-2 sm:mb-3">{stat.number}</p>
                <p className="text-gray-300 text-xs sm:text-sm lg:text-base font-semibold leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Safety Message Below Stats */}
          <div className="mt-8 sm:mt-10 text-center">
            <div className="inline-flex items-center gap-2 bg-wefaqna-gold/20 backdrop-blur-sm rounded-full px-4 sm:px-6 py-3 border border-wefaqna-gold/30">
              <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-gold" />
              <span className="text-wefaqna-gold text-xs sm:text-sm font-bold">{t('landing.numbersRealNote')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Halal Scheduled Call - 2026 Feature Showcase */}
      <section
        className="py-14 sm:py-18 lg:py-24 bg-gradient-to-br from-wefaqna-navy via-wefaqna-navy to-wefaqna-navy/90 relative overflow-hidden"
        data-testid="scheduled-call-section"
      >
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, rgba(174, 213, 129, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px',
          }}></div>
        </div>
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-wefaqna-green/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-wefaqna-gold/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* "Exclusively new 2026" pill */}
          <div className="flex justify-center mb-6 sm:mb-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-wefaqna-gold/30 via-wefaqna-gold/20 to-wefaqna-green/30 backdrop-blur-sm rounded-full px-5 sm:px-7 py-2.5 border-2 border-wefaqna-gold/50 shadow-2xl shadow-wefaqna-gold/10">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-gold" />
              <span className="text-wefaqna-gold text-xs sm:text-sm font-bold tracking-wide">{t('landing.newExclusive2026')}</span>
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-gold" />
            </div>
          </div>

          {/* Headline + Subhead */}
          <div className="text-center mb-10 sm:mb-14 max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-5 sm:mb-6 leading-tight">
              {t('landing.scheduledCallHeading')}<br className="hidden sm:block" />
              <span className="text-wefaqna-green">{t('landing.beforeRealMeeting')}</span>
            </h2>
            <p className="text-gray-200 text-base sm:text-lg lg:text-xl leading-relaxed font-medium px-2">
              {t('landing.scheduledCallSubheading')}
            </p>
          </div>

          {/* Visual mockup + 3 trust pillars */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-10 sm:mb-12">
            {/* CSS-only call card mockup */}
            <div className="relative order-2 lg:order-1">
              <div className="relative mx-auto max-w-sm">
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-[2.5rem] p-5 sm:p-6 shadow-2xl ring-4 ring-wefaqna-green/30 border-4 border-wefaqna-navy/10">
                  {/* status bar */}
                  <div className="flex items-center justify-between text-[10px] text-gray-400 mb-4 px-1">
                    <span className="font-semibold">9:41</span>
                    <div className="flex items-center gap-1">
                      <span>5G</span>
                      <span>●●●●</span>
                    </div>
                  </div>

                  {/* schedule header */}
                  <div className="text-center mb-5">
                    <div className="inline-flex items-center gap-1.5 bg-wefaqna-green/15 rounded-full px-3 py-1 mb-3">
                      <Calendar className="h-3 w-3 text-wefaqna-forest" />
                      <span className="text-[10px] font-bold text-wefaqna-forest">{t('landing.mockCallScheduledChip')}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1">{t('landing.mockCallFriday')}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-wefaqna-navy">{t('landing.mockCallTime')}</p>
                  </div>

                  {/* avatars row */}
                  <div className="flex items-center justify-center gap-3 mb-5">
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-wefaqna-navy to-wefaqna-navy/70 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-wefaqna-green/30 shadow-lg">{t('landing.mockAhmedInitial')}</div>
                      <span className="text-[10px] text-gray-600 font-semibold">{t('landing.mockAhmedName')}</span>
                    </div>
                    <div className="flex-1 relative h-px">
                      <div className="absolute inset-0 border-t-2 border-dashed border-wefaqna-green/40"></div>
                      <Heart className="h-5 w-5 text-wefaqna-forest fill-wefaqna-green absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-0.5" />
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-wefaqna-green to-wefaqna-green/70 rounded-full flex items-center justify-center text-wefaqna-navy text-sm font-bold ring-2 ring-wefaqna-navy/20 shadow-lg">{t('landing.mockSarahInitial')}</div>
                      <span className="text-[10px] text-gray-600 font-semibold">{t('landing.mockSarahName')}</span>
                    </div>
                  </div>

                  {/* wali presence chip */}
                  <div className="flex items-center gap-2 bg-wefaqna-gold/10 rounded-lg px-3 py-2 mb-4 border border-wefaqna-gold/30">
                    <UserCheck className="h-4 w-4 text-wefaqna-gold flex-shrink-0" />
                    <span className="text-[11px] font-semibold text-wefaqna-navy">{t('landing.mockGuardianPresent')}</span>
                    <CheckCircle2 className="h-3.5 w-3.5 text-wefaqna-forest ms-auto" />
                  </div>

                  {/* join button */}
                  <div className="w-full bg-wefaqna-forest text-white font-bold rounded-2xl py-3 shadow-lg flex items-center justify-center gap-2 text-sm">
                    <PhoneCall className="h-5 w-5" />
                    {t('landing.joinCallBtn')}
                  </div>
                </div>

                {/* floating accents */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-wefaqna-gold/40 rounded-full blur-md animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-wefaqna-green/40 rounded-full blur-md animate-pulse"></div>
              </div>
            </div>

            {/* 3 trust pillars */}
            <div className="order-1 lg:order-2 space-y-4 sm:space-y-5">
              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border-2 border-wefaqna-green/20 hover:border-wefaqna-green/50 transition-all duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-wefaqna-green/20 rounded-xl flex items-center justify-center flex-shrink-0 ring-2 ring-wefaqna-green/30">
                  <UserCheck className="h-6 w-6 sm:h-7 sm:w-7 text-wefaqna-forest" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5 leading-tight">{t('landing.pillarGuardianTitle')}</h3>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{t('landing.pillarGuardianDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border-2 border-wefaqna-green/20 hover:border-wefaqna-green/50 transition-all duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-wefaqna-green/20 rounded-xl flex items-center justify-center flex-shrink-0 ring-2 ring-wefaqna-green/30">
                  <Mic className="h-6 w-6 sm:h-7 sm:w-7 text-wefaqna-forest" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5 leading-tight">{t('landing.pillarAudioOnlyTitle')}</h3>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{t('landing.pillarAudioOnlyDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border-2 border-wefaqna-green/20 hover:border-wefaqna-green/50 transition-all duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-wefaqna-green/20 rounded-xl flex items-center justify-center flex-shrink-0 ring-2 ring-wefaqna-green/30">
                  <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7 text-wefaqna-forest" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5 leading-tight">{t('landing.pillarReportTitle')}</h3>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{t('landing.pillarReportDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-2xl mx-auto mb-5">
            <Button
              onClick={() => navigate('/mukalama-shareya')}
              className="w-full sm:w-auto bg-wefaqna-forest text-white hover:bg-wefaqna-forest-hover font-bold text-base sm:text-lg rounded-2xl px-8 py-5 sm:py-6 shadow-2xl shadow-wefaqna-green/30 hover:shadow-wefaqna-green/50 transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              data-testid="scheduled-call-learn-more-btn"
            >
              <span>{t('landing.learnMore')}</span>
              <ChevronLeft className="h-5 w-5 rtl-flip" />
            </Button>
            <a
              href={playStoreUrl({ medium: 'site_scheduled_call_cta' })}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto border-2 border-white/40 text-white hover:bg-white hover:text-wefaqna-navy font-semibold text-base sm:text-lg rounded-2xl px-8 py-5 sm:py-6 backdrop-blur-sm transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2"
              data-testid="scheduled-call-download-btn"
            >
              {t('landing.downloadAppNowBtn')}
            </a>
          </div>

          {/* Honest availability note */}
          <p className="text-center text-xs sm:text-sm text-gray-400 font-medium flex items-center justify-center gap-2">
            <Clock className="h-3.5 w-3.5" />
            <span>{t('landing.androidAvailableIosSoon')}</span>
          </p>
        </div>
      </section>

      {/* Real members' stories: renders nothing until one is published. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SuccessStoriesStrip />
      </div>

      {/* How It Works Section - Clear Steps */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-wefaqna-navy mb-4">
              {t('landing.howItWorksHeading')}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
              {t('landing.howItWorksTagline')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* For Men */}
            <Card className="card-wefaqna border-2 border-wefaqna-navy/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                <div className="flex items-center gap-4 mb-6 sm:mb-8">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-wefaqna-navy rounded-2xl flex items-center justify-center shadow-lg">
                    <User className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-wefaqna-navy">{t('landing.forMenHeading')}</h3>
                </div>
                <ol className="space-y-5 sm:space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-navy text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">1</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forMenStep1Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forMenStep1Body')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-navy text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">2</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forMenStep2Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forMenStep2Body')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-navy text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">3</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forMenStep3Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forMenStep3Body')}</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* For Women */}
            <Card className="card-wefaqna border-2 border-wefaqna-green/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] bg-gradient-to-br from-white to-wefaqna-green/5">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                <div className="flex items-center gap-4 mb-6 sm:mb-8">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-wefaqna-green rounded-2xl flex items-center justify-center shadow-lg">
                    <Users className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-navy" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-wefaqna-navy">{t('landing.forWomenHeading')}</h3>
                </div>
                <ol className="space-y-5 sm:space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-forest text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">1</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forWomenStep1Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forWomenStep1Body')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-forest text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">2</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forWomenStep2Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forWomenStep2Body')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-wefaqna-forest text-white rounded-full flex items-center justify-center text-base font-bold flex-shrink-0 shadow-md">3</div>
                    <div>
                      <p className="text-wefaqna-navy font-semibold text-base sm:text-lg mb-1">{t('landing.forWomenStep3Title')}</p>
                      <p className="text-gray-600 text-sm sm:text-base">{t('landing.forWomenStep3Body')}</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Safety & Privacy Section - COMPREHENSIVE */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white via-wefaqna-green/5 to-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, rgba(51, 62, 72, 0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-wefaqna-green/20 to-wefaqna-green/10 backdrop-blur-sm rounded-full px-5 sm:px-7 py-3 mb-6 border-2 border-wefaqna-green/30 shadow-lg">
              <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-wefaqna-forest" />
              <span className="text-wefaqna-navy text-sm sm:text-base font-bold">{t('landing.safetyBadge')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-wefaqna-navy mb-5 sm:mb-6 leading-tight">
              {t('landing.safetyHeading')}
            </h2>
            <p className="text-gray-700 text-base sm:text-lg lg:text-xl max-w-4xl mx-auto leading-relaxed font-medium">
              {t('landing.safetyTagline')}
            </p>
          </div>

          {/* Safety Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7 mb-12 sm:mb-16">
            {safetyFeatures.map((feature, index) => (
              <Card
                key={index}
                className={`group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-gray-200 hover:border-wefaqna-green/50 bg-gradient-to-br ${feature.color} backdrop-blur-sm`}
              >
                <CardContent className="p-6 sm:p-7 lg:p-8 text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg border-2 border-wefaqna-green/20 mx-auto">
                    <feature.icon className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-navy" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-wefaqna-navy mb-3 leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Guarantee Box */}
          <div className="bg-gradient-to-br from-wefaqna-navy via-wefaqna-navy to-wefaqna-navy/90 rounded-3xl p-8 sm:p-10 lg:p-12 shadow-2xl border-4 border-wefaqna-green/30 relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 50px, rgba(174, 213, 129, 0.1) 50px, rgba(174, 213, 129, 0.1) 100px)`,
              }}></div>
            </div>
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-wefaqna-green rounded-full mb-6 shadow-2xl ring-4 ring-wefaqna-green/30">
                <ShieldCheck className="h-8 w-8 sm:h-10 sm:w-10 text-wefaqna-navy" />
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-5">
                {t('landing.safetyGuaranteeTitle')}
              </h3>
              <p className="text-gray-200 text-base sm:text-lg lg:text-xl mb-6 max-w-3xl mx-auto leading-relaxed">
                {t('landing.safetyGuaranteeDesc')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <CheckCircle2 className="h-5 w-5 text-wefaqna-green" />
                  <span className="text-white text-sm sm:text-base font-semibold">{t('landing.trustFullEncryption')}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <CheckCircle2 className="h-5 w-5 text-wefaqna-green" />
                  <span className="text-white text-sm sm:text-base font-semibold">{t('landing.trustNoPhotosEver')}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <CheckCircle2 className="h-5 w-5 text-wefaqna-green" />
                  <span className="text-white text-sm sm:text-base font-semibold">{t('landing.trustPriorApproval')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Parents Trust Us - Specifically for Daughters */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 bg-wefaqna-navy/10 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 mb-4 border-2 border-wefaqna-navy/20">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-navy" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-bold">{t('landing.trustBadge')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-wefaqna-navy mb-4">
              {t('landing.trustHeading')}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
              {t('landing.trustTagline')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-10">
            {parentTrustPoints.map((point, index) => (
              <Card
                key={index}
                className="card-wefaqna border-2 border-wefaqna-green/30 hover:border-wefaqna-green hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-white to-wefaqna-green/5"
              >
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-4 sm:gap-5">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-wefaqna-green/20 rounded-xl flex items-center justify-center flex-shrink-0 ring-4 ring-wefaqna-green/10">
                      <point.icon className="h-6 w-6 sm:h-7 sm:w-7 text-wefaqna-forest" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-wefaqna-navy mb-2 leading-tight">
                        {point.title}
                      </h3>
                      <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Parent Reassurance Message */}
          <div className="bg-gradient-to-r from-wefaqna-green/10 via-wefaqna-green/5 to-wefaqna-green/10 rounded-2xl p-6 sm:p-8 lg:p-10 border-2 border-wefaqna-green/30 shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-wefaqna-forest rounded-full flex items-center justify-center shadow-2xl flex-shrink-0 ring-4 ring-wefaqna-green/30">
                <Heart className="h-10 w-10 sm:h-12 sm:w-12 text-white fill-white" />
              </div>
              <div className="text-center md:text-end flex-1">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-wefaqna-navy mb-3">
                  {t('landing.parentReassuranceTitle')}
                </h3>
                <p className="text-gray-700 text-sm sm:text-base lg:text-lg leading-relaxed">
                  {t('landing.parentReassuranceDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Trust Building */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-wefaqna-navy mb-4">
              {t('landing.whyChooseHeading')}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
              {t('landing.whyChooseTagline')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="card-wefaqna group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-wefaqna-green/30"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-5 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="h-8 w-8 sm:h-10 sm:w-10 text-wefaqna-navy" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-wefaqna-navy mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - More Trust */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 bg-wefaqna-green/10 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 mb-4 border-2 border-wefaqna-green/20">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-wefaqna-forest" />
              <span className="text-wefaqna-navy text-xs sm:text-sm font-bold">{t('landing.testimonialsBadge')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-wefaqna-navy mb-4">
              {t('landing.testimonialsHeading')}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto">
              {t('landing.testimonialsSubtitle')}
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-8 overflow-x-auto md:overflow-visible pb-4 md:pb-0 snap-x snap-mandatory md:snap-none -mx-4 px-4 md:mx-0 md:px-0">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="card-wefaqna hover:shadow-2xl transition-all duration-300 md:hover:scale-105 border-2 border-wefaqna-green/20 hover:border-wefaqna-green/40 bg-white flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-auto snap-center"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-5 sm:p-6 lg:p-7">
                  <div className="flex items-start justify-between mb-4">
                    <Quote className="h-8 w-8 sm:h-10 sm:w-10 text-wefaqna-green/30" />
                    <div className="flex items-center gap-1">
                      <Shield className="h-4 w-4 text-wefaqna-forest" />
                      <span className="text-xs text-wefaqna-forest font-semibold">{t('landing.verifiedBadge')}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed font-medium min-h-[80px]">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-wefaqna-green to-wefaqna-green/70 rounded-full flex items-center justify-center shadow-md ring-2 ring-wefaqna-green/20">
                      <span className="text-white font-bold text-base sm:text-lg">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-bold text-wefaqna-navy text-sm sm:text-base">{testimonial.author}</p>
                      <p className="text-gray-500 text-xs sm:text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust Message */}
          <div className="text-center bg-wefaqna-navy/5 rounded-2xl p-6 sm:p-8 border-2 border-wefaqna-navy/10">
            <ShieldCheck className="h-10 w-10 sm:h-12 sm:w-12 text-wefaqna-forest mx-auto mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold text-wefaqna-navy mb-3">
              {t('landing.testimonialsTrustTitle')}
            </h3>
            <p className="text-gray-700 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              {t('landing.testimonialsTrustDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Community / Follow Us Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-wefaqna-navy mb-3">
            {t('landing.communityHeading')}
          </h2>
          <p className="text-gray-600 text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('landing.communitySubheading')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto">
            <a
              href="https://whatsapp.com/channel/0029VbCa0iZJkK7FjKLvm637"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 sm:p-5 rounded-2xl border-2 border-gray-200 hover:border-wefaqna-green hover:bg-wefaqna-green/5 transition-all duration-200"
              aria-label={t('footer.followWhatsappAria')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-forest" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
              </svg>
              <span className="text-sm sm:text-base font-semibold text-wefaqna-navy">{t('landing.socialWhatsappTitle')}</span>
              <span className="text-xs text-gray-500">{t('landing.socialWhatsappDesc')}</span>
            </a>

            <a
              href="https://t.me/wefaqna"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 sm:p-5 rounded-2xl border-2 border-gray-200 hover:border-wefaqna-green hover:bg-wefaqna-green/5 transition-all duration-200"
              aria-label={t('footer.followTelegramAria')}
            >
              <Send className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-forest" />
              <span className="text-sm sm:text-base font-semibold text-wefaqna-navy">{t('landing.socialTelegramTitle')}</span>
              <span className="text-xs text-gray-500">{t('landing.socialTelegramDesc')}</span>
            </a>

            <a
              href="https://www.instagram.com/wefaqna"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 sm:p-5 rounded-2xl border-2 border-gray-200 hover:border-wefaqna-green hover:bg-wefaqna-green/5 transition-all duration-200"
              aria-label={t('footer.followInstagramAria')}
            >
              <Instagram className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-forest" />
              <span className="text-sm sm:text-base font-semibold text-wefaqna-navy">{t('landing.socialInstagramTitle')}</span>
              <span className="text-xs text-gray-500">{t('landing.socialInstagramDesc')}</span>
            </a>

            <a
              href="https://www.facebook.com/share/1Euqk2SH4b/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 sm:p-5 rounded-2xl border-2 border-gray-200 hover:border-wefaqna-green hover:bg-wefaqna-green/5 transition-all duration-200"
              aria-label={t('footer.followFacebookAria')}
            >
              <Facebook className="h-7 w-7 sm:h-8 sm:w-8 text-wefaqna-forest" />
              <span className="text-sm sm:text-base font-semibold text-wefaqna-navy">{t('landing.socialFacebookTitle')}</span>
              <span className="text-xs text-gray-500">{t('landing.socialFacebookDesc')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Essential guides — internal links from the homepage (highest-authority
          page) to the pillar articles, for SEO link-equity + reader discovery.
          Real <Link> anchors so Google crawls them. Titles pulled from the
          article data so they never drift. */}
      <section className="py-12 sm:py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-wefaqna-navy text-center mb-2">
            {t('landing.guidesTitle', 'أدلة أساسية قبل الزواج')}
          </h2>
          <p className="text-center text-gray-600 mb-8">
            {t('landing.guidesSubtitle', 'اقرأ قبل ما تبدأ رحلتك')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {['al-istikhara-qabl-al-zawaj', 'mada-bayn-al-khotba-wa-al-zawaj', 'mawqi-zawaj-halal-majani-masr']
              .map((s) => articlesBySlug[s])
              .filter(Boolean)
              .map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="block bg-white rounded-xl border border-gray-200 p-5 hover:border-wefaqna-green hover:shadow-md transition-all"
                >
                  <h3 className="font-bold text-wefaqna-navy text-sm leading-relaxed mb-2">{a.title}</h3>
                  <span className="text-xs text-wefaqna-forest font-semibold">
                    {t('landing.guidesRead', 'اقرأ المقال ←')}
                  </span>
                </Link>
              ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/blog" className="text-sm font-semibold text-wefaqna-navy hover:text-wefaqna-forest underline">
              {t('landing.guidesAll', 'كل المقالات')}
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Strong Close */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-wefaqna-navy via-wefaqna-navy to-wefaqna-navy/90 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, rgba(174, 213, 129, 0.2) 1px, transparent 1px)`,
            backgroundSize: '30px 30px',
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-wefaqna-green rounded-full mb-6 sm:mb-8 shadow-2xl shadow-wefaqna-green/30">
            <Heart className="h-8 w-8 sm:h-10 sm:w-10 text-wefaqna-navy fill-wefaqna-navy animate-pulse" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            {t('landing.ctaSection')}
          </h2>

          <p className="text-gray-300 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('landing.ctaSubtagline')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto mb-8">
            <Button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto bg-wefaqna-forest text-white hover:bg-wefaqna-forest-hover font-bold text-base sm:text-lg rounded-2xl px-8 sm:px-12 py-6 sm:py-7 shadow-2xl shadow-wefaqna-green/30 hover:shadow-wefaqna-green/50 transition-all duration-300 hover:scale-105 active:scale-95"
              data-testid="cta-register-btn"
            >
              <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 ms-2" />
              {t('landing.ctaRegister')}
            </Button>

            <Button
              onClick={() => navigate('/faq')}
              variant="outline"
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-wefaqna-navy font-semibold text-base sm:text-lg rounded-2xl px-8 sm:px-12 py-6 sm:py-7 backdrop-blur-sm transition-all duration-300 hover:scale-105 active:scale-95"
              data-testid="cta-learn-more-btn"
            >
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 ms-2" />
              {t('landing.learnMoreBtn')}
            </Button>
          </div>

          <div className="inline-flex items-center gap-2 bg-wefaqna-gold/20 backdrop-blur-sm rounded-full px-6 py-3 border border-wefaqna-gold/30">
            <Clock className="h-5 w-5 text-wefaqna-gold" />
            <p className="text-wefaqna-gold font-semibold text-sm sm:text-base">
              {t('landing.islamicBlessing')}
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
